import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Row,
  Col,
  InputGroup,
  Form,
  Button,
  Badge,
} from "react-bootstrap";
import {
  IconSearch,
  IconFilter,
  IconFilterOff,
  IconUsers,
  IconSortAscending,
} from "@tabler/icons-react";
import Select from "react-select";
import { debounce } from "lodash";

import { listBasicAgents } from "../../../services/AgentsServices";
import ProductsServices from "../../../services/ProductsServices";

const ProductSearch = ({
  searchTerm,
  agentFilter,
  statusFilter,
  orderingFilter,
  onSearch,
  onAgentFilter,
  onStatusFilter,
  onOrderingFilter,
  totalCount,
  hideAgentFilter = false,
  hubMode = false,
}) => {
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);
  const [agentOptions, setAgentOptions] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);

  // Debounce para a pesquisa
  const debouncedSearch = useCallback(
    debounce((value) => {
      // Só chamar onSearch se o valor realmente mudou
      if (value !== searchTerm) {
        onSearch(value);
      }
    }, 500),
    [onSearch, searchTerm]
  );

  useEffect(() => {
    loadAgents();
  }, []);

  useEffect(() => {
    setLocalSearchTerm(searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    // Só executar o debounce se o valor realmente mudou e não for a inicialização
    if (localSearchTerm !== searchTerm) {
      debouncedSearch(localSearchTerm);
    }
    return () => {
      debouncedSearch.cancel();
    };
  }, [localSearchTerm, debouncedSearch, searchTerm]);

  const loadAgents = async () => {
    try {
      setAgentsLoading(true);
      const response = await listBasicAgents();

      if (response.success) {
        const options = [
          { value: "", label: "Todos os agentes" },
          ...response.agents.map((agent) => ({
            value: agent.id,
            label: `${agent.agent_name} (${agent.agent_job})`,
            agent_name: agent.agent_name,
            agent_job: agent.agent_job,
          })),
        ];
        setAgentOptions(options);
      }
    } catch (error) {
      console.error("Erro ao carregar agentes:", error);
      setAgentOptions([{ value: "", label: "Todos os agentes" }]);
    } finally {
      setAgentsLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setLocalSearchTerm(e.target.value);
  };

  const handleAgentChange = (selectedOption) => {
    onAgentFilter(selectedOption?.value || "");
  };

  const handleStatusChange = (e) => {
    onStatusFilter(e.target.value);
  };

  const handleOrderingChange = (e) => {
    onOrderingFilter(e.target.value);
  };

  const clearFilters = () => {
    setLocalSearchTerm("");
    onSearch("");
    onAgentFilter("");
    onStatusFilter("");
    onOrderingFilter("-created_at");
  };

  const hasActiveFilters = () => {
    return (
      searchTerm ||
      agentFilter ||
      statusFilter ||
      orderingFilter !== "-created_at"
    );
  };

  const getSelectedAgent = () => {
    return agentOptions.find((option) => option.value === agentFilter) || null;
  };

  const availableFilters = ProductsServices.getAvailableFilters();

  return (
    <Card className="mb-4">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0 d-flex align-items-center">
            <IconFilter size={16} className="me-2" />
            Filtros e Busca
          </h6>
          <div className="d-flex align-items-center gap-2">
            {totalCount > 0 && (
              <Badge bg="info">
                {totalCount} produto{totalCount !== 1 ? "s" : ""} encontrado
                {totalCount !== 1 ? "s" : ""}
              </Badge>
            )}
            {hasActiveFilters() && (
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={clearFilters}
              >
                <IconFilterOff size={14} className="me-1" />
                Limpar
              </Button>
            )}
          </div>
        </div>

        <Row>
          <Col xs={12} md={4} className="mb-3">
            <Form.Label className="small text-muted">Buscar produto</Form.Label>
            <InputGroup>
              <InputGroup.Text>
                <IconSearch size={16} />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Nome, descrição..."
                value={localSearchTerm}
                onChange={handleSearchChange}
              />
            </InputGroup>
          </Col>

          {!hideAgentFilter && (
            <Col xs={12} md={3} className="mb-3">
              <Form.Label className="small text-muted">
                Filtrar por agente
              </Form.Label>
              <div className="position-relative">
                <IconUsers
                  size={16}
                  className="position-absolute"
                  style={{
                    top: "50%",
                    left: "8px",
                    transform: "translateY(-50%)",
                    zIndex: 10,
                    color: "#6c757d",
                  }}
                />
                <Select
                  value={getSelectedAgent()}
                  onChange={handleAgentChange}
                  options={agentOptions}
                  placeholder="Todos os agentes"
                  isLoading={agentsLoading}
                  isClearable
                  isSearchable
                  noOptionsMessage={() => "Nenhum agente encontrado"}
                  styles={{
                    control: (base) => ({
                      ...base,
                      paddingLeft: "24px",
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      paddingLeft: "24px",
                    }),
                    placeholder: (base) => ({
                      ...base,
                      paddingLeft: "24px",
                    }),
                  }}
                />
              </div>
            </Col>
          )}

          <Col xs={12} md={3} className="mb-3">
            <Form.Label className="small text-muted">Ordenar por</Form.Label>
            <div className="position-relative">
              <IconSortAscending
                size={16}
                className="position-absolute"
                style={{
                  top: "50%",
                  left: "8px",
                  transform: "translateY(-50%)",
                  zIndex: 10,
                  color: "#6c757d",
                }}
              />
              <Form.Select
                value={orderingFilter}
                onChange={handleOrderingChange}
                style={{ paddingLeft: "32px" }}
              >
                {availableFilters.ordering.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </div>
          </Col>
        </Row>

        {/* Filtros ativos */}
        {hasActiveFilters() && searchTerm && (
          <div className="mt-3 pt-3 border-top">
            <div className="d-flex flex-wrap gap-2 align-items-center">
                <>
                  <small className="text-muted me-2">Filtros ativos:</small>
                  <Badge bg="primary" className="d-flex align-items-center">
                    <IconSearch size={12} className="me-1" />
                    Busca: "{searchTerm}"
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 ms-1 text-white"
                      onClick={() => {
                        setLocalSearchTerm("");
                        onSearch("");
                      }}
                      style={{ fontSize: "12px" }}
                    >
                      ×
                    </Button>
                  </Badge>
                </>

              {orderingFilter !== "-created_at" && (
                <Badge bg="warning" className="d-flex align-items-center">
                  <IconSortAscending size={12} className="me-1" />
                  Ordem:{" "}
                  {
                    availableFilters.ordering.find(
                      (o) => o.value === orderingFilter
                    )?.label
                  }
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 ms-1 text-dark"
                    onClick={() => onOrderingFilter("-created_at")}
                    style={{ fontSize: "12px" }}
                  >
                    ×
                  </Button>
                </Badge>
              )}
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default ProductSearch;
